"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useLoadProducts = void 0;
const react_1 = require("react");
const predicate_language_1 = require("@groupby/predicate-language");
const debounce_1 = __importDefault(require("debounce"));
const react_redux_1 = require("react-redux");
const formal_attributes_1 = require("../formal-attributes");
const constants_1 = require("../../constants");
const hooks_1 = require("../../hooks");
const localization_1 = require("../../localization");
const store_1 = require("../../store");
const useLoadProducts = ({ currentPage, filter, rowsPerPage, setTotalCount, sortParams, }) => {
    const dispatch = (0, react_redux_1.useDispatch)();
    const { formatMessage: t } = (0, localization_1.useLocalization)();
    const client = (0, hooks_1.useEnrichClient)();
    const token = (0, hooks_1.useToken)();
    const { addError } = (0, hooks_1.useNotification)();
    const [currentOrganizationsIds] = (0, store_1.useSelectedOrganizationsIdsSelector)();
    const [isLoading, setIsLoading] = (0, react_1.useState)(true);
    const [products, setProducts] = (0, react_1.useState)([]);
    const { loadFormalAttributes, getAttributeName, getAttributeValueName, } = (0, formal_attributes_1.useFormalAttributes)();
    const getProducts = () => __awaiter(void 0, void 0, void 0, function* () {
        const data = yield client.products.getPage({
            filter,
            page: { limit: rowsPerPage, offset: (currentPage - 1) * rowsPerPage },
            sort: sortParams,
        });
        setProducts(data.toArray());
        dispatch((0, store_1.productsSetTotal)(data.total));
    });
    const loadProducts = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setIsLoading(true);
            yield (setTotalCount === null || setTotalCount === void 0 ? void 0 : setTotalCount());
            if (currentOrganizationsIds) {
                yield getProducts();
            }
            else {
                setProducts([]);
            }
            setIsLoading(false);
        }
        catch (_a) {
            addError(t({ key: 'PRODUCT_EDITOR_TABLE_LOADING_ERROR' }));
            setIsLoading(false);
        }
    });
    const latestLoadProducts = (0, hooks_1.useLatest)(loadProducts);
    const debouncedLoadProducts = (0, react_1.useCallback)(() => (0, debounce_1.default)(() => {
        void latestLoadProducts.current();
    }, constants_1.SEARCH_QUERY_UPDATE_DELAY)(), [latestLoadProducts]);
    const updateProducts = (0, react_1.useCallback)((productIds) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const products = yield client.products.getMany({ filter: (0, predicate_language_1.where)((0, predicate_language_1.f) `id`, '@<', productIds.map(predicate_language_1.uuid)) });
            const productMap = new Map(products.map((item) => [item.id, item]));
            setProducts((productList) => productList.map((item) => { var _a; return (Object.assign({}, (_a = productMap.get(item.id)) !== null && _a !== void 0 ? _a : item)); }));
        }
        catch (e) {
            addError(t({ key: 'PRODUCT_LOADING_ERROR' }));
        }
    }), [addError, client, t]);
    (0, react_1.useEffect)(() => {
        if (token) {
            void debouncedLoadProducts();
        }
        return () => {
            dispatch((0, store_1.productsSetTotal)(0));
        };
    }, [currentPage, filter, rowsPerPage, sortParams, debouncedLoadProducts, token, dispatch]);
    (0, react_1.useEffect)(() => {
        if (products.length) {
            const actualAttributes = [];
            products.forEach(({ attributes = [] }) => {
                attributes.forEach(({ id, values }) => {
                    actualAttributes.push({ attributeId: id });
                    values.forEach(({ id: valueId, name }) => {
                        // EN-2993 Attribute value name and id sometimes both null
                        if (valueId) {
                            actualAttributes.push({ attributeId: id, valueId, valueName: name });
                        }
                    });
                });
            });
            void loadFormalAttributes(actualAttributes, { highPriority: true });
        }
    }, [products, loadFormalAttributes]);
    const productsWithAttributes = (0, react_1.useMemo)(() => products.map((_a) => {
        var { attributes } = _a, product = __rest(_a, ["attributes"]);
        const newAttributes = attributes.map((_a) => {
            var { values } = _a, attribute = __rest(_a, ["values"]);
            return (Object.assign(Object.assign({}, attribute), { name: getAttributeName(attribute.id), values: values.map((_a) => {
                    var { name } = _a, value = __rest(_a, ["name"]);
                    return (Object.assign(Object.assign({}, value), { name: name !== null && name !== void 0 ? name : getAttributeValueName(value.id) }));
                }) }));
        });
        return Object.assign(Object.assign({}, product), { attributes: newAttributes });
    }), [getAttributeName, getAttributeValueName, products]);
    return {
        isLoading,
        loadProducts: debouncedLoadProducts,
        products: productsWithAttributes,
        updateProducts,
    };
};
exports.useLoadProducts = useLoadProducts;
